/**
 * useArTableOffPageBundleHeader
 *
 * Supplies `resolveArBundleDisplay`'s `offPageHeaders` from `ArTableBody`: the
 * header document of the one bundle a page boundary can cut open, keyed by
 * bundleId. A bundle is a contiguous block, so only the run of members at the
 * top of a page can be headerless, and all of it belongs to the first row's
 * bundle — one document restores its identity (SYD-2430).
 *
 * One fact the code cannot state for itself:
 * - What keeps the PDF export failing closed (SYD-2279) is the WINDOW test
 *   `rowsDocs.has(bundleId)` below, mirrored in `resolveArBundleDisplay`: a
 *   header the user did not tick is still on the page, so no off-page header is
 *   read and the member keeps no bundle identity. `pageRowIds` is
 *   `visibleRowIds` because that IS the page in query order, not a narrowing of
 *   it — substituting the render plan does not move the guard.
 */

import { useMemo } from 'react';
import { useStoreDocsByPaths } from '@/store';
import type { DocEntry } from '@/store/Store.types';
import type { ArRowData, ArRowId } from '#types';

type UseArTableOffPageBundleHeaderParams = {
  rowsPath: string;
  /** The page in query order, un-narrowed — `visibleRowIds`, never `filteredRowIds`. */
  pageRowIds: readonly ArRowId[];
  /** `null` when bundles are off; the hook then subscribes to nothing. */
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>> | null;
};

const NO_OFF_PAGE_HEADERS: ReadonlyMap<string, ArRowData> = new Map<string, ArRowData>();

function useArTableOffPageBundleHeader({
  rowsPath,
  pageRowIds,
  rowsDocs,
}: UseArTableOffPageBundleHeaderParams): ReadonlyMap<string, ArRowData> {
  const firstRowId = pageRowIds[0];
  const firstRow = rowsDocs && firstRowId !== undefined
    ? rowsDocs.get(firstRowId)?.data
    : undefined;
  /**
   * A header's docId IS its bundleId and `rowsDocs` is keyed by docId, so
   * `has` is an exact "the header is on this page". It is also what keeps a
   * transient column sort safe, where a member can precede its own header.
   */
  const offPageBundleId = rowsDocs
    && firstRow?.bundleRole === 'member'
    && firstRow.bundleId !== undefined
    && !rowsDocs.has(firstRow.bundleId)
    ? firstRow.bundleId
    : undefined;

  const paths = useMemo(
    () => (offPageBundleId === undefined ? [] : [`${rowsPath}/${offPageBundleId}`]),
    [offPageBundleId, rowsPath],
  );

  const { data } = useStoreDocsByPaths<ArRowData>(paths);

  return useMemo(() => {
    if (offPageBundleId === undefined) return NO_OFF_PAGE_HEADERS;
    const header = data.get(`${rowsPath}/${offPageBundleId}`);
    if (!header) return NO_OFF_PAGE_HEADERS;
    return new Map([[offPageBundleId, header]]);
  }, [data, offPageBundleId, rowsPath]);
}

export { useArTableOffPageBundleHeader };
